import { Button, buttonMaxWidth } from '@/src/common/components/Button'
import { Routes } from '@/src/common/enums/routes'
import Feather from '@expo/vector-icons/Feather'
import { View, Text, StyleSheet } from 'react-native'
import { useMutation } from '@apollo/client'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { JOIN_WAITLIST, VERIFY_CARD_INVITE_CODE } from '@/src/common/gql/mutations/card'
import { GET_WAITLIST_STATUS } from '@/src/common/gql/queries/card'
import { Check } from '@/src/assets/icons/Check'
import { type ComponentProps, useCallback, useEffect, useMemo, useState } from 'react'
import { Loading } from '@/src/common/components/Loading'
import { useLogError } from '@/src/common/hooks/useLogError'
import { JoinWaitlistResponseStatus } from '@api/mesa/graphql'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { Link, Redirect, useFocusEffect, router } from 'expo-router'
import { Input } from '@/src/common/components/Input'
import { triggerSegmentAction } from '@/src/services/segment'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import { useCardDisclosure } from '@/src/services/Statsig/useCardDisclosureParameterStore'
import { MesaHeader } from '@/src/common/modules/RewardsSection/MesaHeader'
import { MesaMonogram } from '@/src/assets/icons/MesaMonogram'
import { MainWrapper } from '@/src/common/wrappers/MainWrapper'
import { MesaCardImage } from '@/src/common/modules/CardSection'
import { Tooltip } from '@/src/components/ui/panels/Tooltip'
import { MesaFooter } from '@/src/common/components/MesaFooter'
import { DesignSystem } from '@/src/design-system'
import { useCardDesignParameterStore } from '@/src/services/Statsig/useCardDesignParameterStore'
import { CardImagesSchema } from '@/src/services/Statsig/useCardDesignParameterStore'
import { mesaColorNames, fontFamilies, typography } from '@/src/common/theme'
import { useTheme } from '@react-navigation/native'
import { EnablePermissionsBanner } from '@/src/experiences/notifications/components/enable-permissions-banner'
import { Spinner } from 'tamagui'
import * as CardApplication from '@/src/experiences/card-application'
import * as Mortgage from '@/src/experiences/mortgage'
import { withCardGate } from '@/src/experiences/card-accounts/withCardGate'
import { PartnerDisclosures } from '@/src/common/components/PartnerDisclosures'

export const mobileAddInstallCtaUrl = 'https://www.mesamember.com/#app'

export const cardApplicationEntryBackgroundColor = mesaColorNames['Greyscale/100 #F5F8F8']

export function MobileAppInstallCta(props: Partial<ComponentProps<typeof Link>>) {
  return (
    <Link
      style={[
        typography['label/md-bold'],
        {
          backgroundColor: mesaColorNames['Acre Turquoise/500 #00DFC9'],
          padding: 16,
          borderRadius: 48,
          alignSelf: 'center',
          color: mesaColorNames['Greyscale/900 #152438'],
          width: '100%',
          textAlign: 'center',
        },
      ]}
      href={mobileAddInstallCtaUrl}
      {...props}
    />
  )
}

function CardFeature({ text }: { text: string }) {
  return (
    <View style={styles.cardFeatureContainer}>
      <View style={styles.cardFeature}>
        <Feather
          name="check"
          color={mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9']}
          style={styles.cardFeatureIcon}
          size={24}
        />
        <DesignSystem.Markdown
          style={{
            body: { color: mesaColorNames['Greyscale/900 #152438'], lineHeight: 24 },
            paragraph: { marginTop: 0, marginBottom: 0 },
          }}
        >
          {text}
        </DesignSystem.Markdown>
      </View>
    </View>
  )
}

export default withCardGate(function CardBenefits({
  applicationStatusInitialized,
  showCardManagement,
  newestApplication,
}) {
  const isMesaCardInviteEnabled = useFeatureGate(FeatureGates.MesaCardInviteEnabled)
  const isMesaCardEnabled = useFeatureGate(FeatureGates.MesaCardEnabled)
  const cardApplicationRoutesEnabled =
    CardApplication.Statsig.FeatureGate.useCardApplicationRoutesEnabled()

  const { colors } = useTheme()
  const [originalNavigationBackgroundColor] = useState(colors.background)
  useEffect(() => {
    colors.background = cardApplicationEntryBackgroundColor
    return () => {
      colors.background = originalNavigationBackgroundColor
    }
  }, [colors, originalNavigationBackgroundColor])

  if (
    !applicationStatusInitialized ||
    isMesaCardInviteEnabled.loadingStatus !== 'Ready' ||
    isMesaCardEnabled.loadingStatus !== 'Ready' ||
    typeof cardApplicationRoutesEnabled !== 'boolean'
  ) {
    return <Loading />
  }

  if (showCardManagement) {
    return <Redirect href="/main/card/card-management" />
  }

  if (newestApplication?.status === 'ISSUED') {
    return <Redirect href="/main/card-application/applicationApproved" />
  }

  return (
    <CardBenefitsGated
      isMesaCardInviteEnabled={isMesaCardInviteEnabled.featureGate.value}
      isMesaCardEnabled={isMesaCardEnabled.featureGate.value}
      cardApplicationRoutesEnabled={cardApplicationRoutesEnabled}
    />
  )
})

export function navigateToApplicationFlow({
  cardApplicationStatus,
  navigateTo,
}: {
  cardApplicationStatus?:
    | { legacy: string }
    | {
        forNewestApplication: NonNullable<
          NonNullable<
            ReturnType<typeof CardApplication.Hooks.useNewestApplication>['data']
          >['newestApplication']
        >['status']
      }
  navigateTo: (path: (typeof Routes)[keyof typeof Routes]['route']) => void
}) {
  if (!cardApplicationStatus) {
    navigateTo(Routes.CardOnboarding.route)
    return
  }

  if ('forNewestApplication' in cardApplicationStatus) {
    switch (cardApplicationStatus.forNewestApplication) {
      case 'STARTED':
      case 'ADMIN_RESET':
        navigateTo(Routes.CardOnboarding.route)
        break
      default:
        navigateTo(
          CardApplication.Navigation.ApplicationStatusRoutes[
            cardApplicationStatus.forNewestApplication
          ].entryRoute.route,
        )
        break
    }
    return
  }

  switch (cardApplicationStatus.legacy) {
    // Even if the card is ISSUED, we want to bring the user to the application processing page because,
    // assuming the user hasn't seen the `ApplicationApproved` screen yet (as is implied by the fact that
    // they're on the `cardBenefits` screen), they'll be redirected to the `ApplicationApproved` screen,
    // which is only navigable from the `ApplicationProcessing` screen. Also, it's fine for the user to
    // see the `ApplicationProcessing`+`ApplicationApproved` screen multiple times, but it _is not_ ok
    // for the user to jump directly to the `CardManagement`, without ever seeing the `ApplicationProcessing`
    // (where they would be informed of their credit limit, APR, etc.)
    case 'ISSUED':
    case 'PENDING': // Application is being processed.
      navigateTo(Routes.ApplicationProcessing.route)
      break
    case 'DENIED':
      navigateTo(Routes.ApplicationDenied.route)
      break
    case 'IN_REVIEW':
      navigateTo(Routes.DocumentUpload.route)
      break
    default:
      navigateTo(Routes.CardOnboarding.route)
  }
}

function CardBenefitsGated({
  isMesaCardInviteEnabled,
  isMesaCardEnabled,
  cardApplicationRoutesEnabled,
}: {
  isMesaCardInviteEnabled: boolean
  isMesaCardEnabled: boolean
  cardApplicationRoutesEnabled: boolean
}) {
  const onError = useLogError()
  const newestApplicationQuery = CardApplication.Hooks.useNewestApplication()
  const { data: cardApplicationStatus, loading: loadingCardApplicationStatus } = useLoggedInQuery(
    CardApplication.GQL.Queries.GET_CARD_APPLICATION_STATUS,
    { fetchPolicy: 'network-only', onError },
  )

  const { data: userData, loading: loadingUserData } = useLoggedInQuery(GET_USER_INFO, {
    fetchPolicy: 'network-only',
  })

  const [loadingBetweenSubmissionAndRefetch, setLoadingBetweenSubmissionAndRefetch] =
    useState(false)

  const [isShowingCardInviteCodeSubmissions, setIsShowingCardInviteCodeSubmissions] =
    useState(false)

  const [showCardApplyButton, setShowCardApplyButton] = useState(isMesaCardEnabled)

  const [verifyCardInviteCode] = useMutation(VERIFY_CARD_INVITE_CODE)

  const [cardInviteCodeInput, setCardInviteCodeInput] = useState<string>('')
  const [cardInviteCodeError, setCardInviteCodeError] = useState<string>('')
  const {
    loading: loadingWaitlistStatus,
    data: waitlistStatus,
    refetch: refetchWaitlistStatus,
  } = useLoggedInQuery(GET_WAITLIST_STATUS, { onError })

  // Reset the invite code input if navigating back so the member can join the waitlist
  useFocusEffect(
    useCallback(() => {
      setIsShowingCardInviteCodeSubmissions(false)
      setCardInviteCodeError('')
      setCardInviteCodeInput('')
    }, []),
  )

  const sourcePartnerName = userData?.getUserInfo.sourcePartnerName ?? 'default'
  const parsedResult = Mortgage.Statsig.Store.MortgageModuleVariantName.safeParse(sourcePartnerName)
  const parsedSourcePartnerName = parsedResult.success ? parsedResult.data : 'default'

  const isWaitlisted = useMemo(
    () => Boolean(waitlistStatus?.getWaitlistStatus.isWaitlisted),
    [waitlistStatus],
  )
  const [joinWaitlistMutation, { loading: loadingJoinWaitlist, data: joinWaitlistResponse }] =
    useMutation(JOIN_WAITLIST, { onError })

  const cardDisclosure = useCardDisclosure()

  function getDisclosureData() {
    if (parsedSourcePartnerName !== 'default') {
      const partnerData = cardDisclosure.get('partner')?.[parsedSourcePartnerName]
      if (partnerData && (partnerData.benefits.length > 0 || partnerData.disclosures.length > 0)) {
        return {
          benefits: partnerData.benefits,
          disclosures: partnerData.disclosures,
        }
      }
    }
    return {
      benefits: cardDisclosure.get('benefits', { benefits: [] }).benefits,
      disclosures: cardDisclosure.get('disclosures', { disclosures: [] }).disclosures,
    }
  }

  const { benefits, disclosures } = getDisclosureData()

  async function verifyInviteCode() {
    try {
      await verifyCardInviteCode({
        variables: { verifyCardInviteCode: { inviteCode: cardInviteCodeInput } },
        onCompleted: (data) => {
          if (data.verifyCardInviteCode.verificationSuccessful) {
            if (data.verifyCardInviteCode.verificationCodeType === 'MASTER') {
              void triggerSegmentAction({
                component: 'Button',
                action:
                  'User Attempted to verify a card invite code that succeeded to a master invite code',
              })
            } else {
              void triggerSegmentAction({
                component: 'Button',
                action:
                  'User Attempted to verify a card invite code that succeeded to a personal invite code',
              })
            }
            setCardInviteCodeError('')
            setShowCardApplyButton(true)
          } else {
            void triggerSegmentAction({
              component: 'Button',
              action: 'User Attempted to verify a card invite code that failed verification',
            })
            setCardInviteCodeError('Please enter a valid invite code')
          }
        },
      })
    } catch (cause) {
      onError(new Error('Error verifying invite code', { cause }))
    }
  }

  useEffect(() => {
    if (userData?.getUserInfo.redeemedCardInviteCode != null) {
      setShowCardApplyButton(true)
    }
  }, [userData])

  useEffect(() => {
    if (joinWaitlistResponse?.joinWaitlist.status === JoinWaitlistResponseStatus.Error) {
      onError(new Error('Failed to join waitlist', { cause: joinWaitlistResponse.joinWaitlist }))
    }
  }, [joinWaitlistResponse, onError])

  const loading =
    newestApplicationQuery.isUninitialized ||
    newestApplicationQuery.isLoading ||
    newestApplicationQuery.isFetching ||
    loadingCardApplicationStatus ||
    loadingWaitlistStatus ||
    loadingJoinWaitlist ||
    loadingBetweenSubmissionAndRefetch ||
    loadingUserData

  const isButtonEnabled = !loading && (showCardApplyButton || !isWaitlisted)

  async function joinWaitlist() {
    try {
      if (!isWaitlisted) {
        setLoadingBetweenSubmissionAndRefetch(true)
        await joinWaitlistMutation()
        await refetchWaitlistStatus()
      }
    } finally {
      setLoadingBetweenSubmissionAndRefetch(false)
    }
  }

  const buttonContent = useMemo(
    () =>
      loading ? (
        <Spinner />
      ) : showCardApplyButton ? (
        <Text style={styles.footerButtonText}>
          {(cardApplicationStatus?.getCardProductApplicationStatus.status &&
            ['PENDING', 'IN_REVIEW'].includes(
              cardApplicationStatus.getCardProductApplicationStatus.status,
            )) ||
          (newestApplicationQuery.data?.newestApplication?.status &&
            newestApplicationQuery.data.newestApplication.status !== 'STARTED')
            ? 'View Current Application Status'
            : 'Apply Now'}
        </Text>
      ) : isWaitlisted ? (
        <View style={styles.footerButtonProcessingWrapper}>
          <Check height={20} width={20} />
          <Text
            style={[styles.footerButtonText, styles.footerButtonTextProcessing]}
          >{` You're on the Waitlist`}</Text>
        </View>
      ) : (
        <Text style={styles.footerButtonText}>Join Waitlist</Text>
      ),
    [
      loading,
      isWaitlisted,
      showCardApplyButton,
      cardApplicationStatus?.getCardProductApplicationStatus.status,
      newestApplicationQuery,
    ],
  )

  // Get Header Text based on the Partner
  const cardDesignStore = useCardDesignParameterStore()
  const headerText = useMemo(() => {
    const cardDesigns = CardImagesSchema.parse(cardDesignStore.get('card_details', {}))
    const partnerName = userData?.getUserInfo.sourcePartnerName?.toLowerCase() ?? 'default'

    return (
      cardDesigns[partnerName]?.header_text ??
      'Earn Mesa Points on mortgage payments*, home upgrades, and every day spending¹'
    )
  }, [cardDesignStore, userData])

  // Disclosures for co-branded partners
  const moduleDesign = Mortgage.Statsig.Store.useMortgageModuleDesign(parsedSourcePartnerName)
  const { disclosure_paragraphs } = moduleDesign

  return (
    <MainWrapper
      screen="/main/card/cardBenefits"
      footer={
        <>
          {disclosure_paragraphs && disclosure_paragraphs.length > 0 && (
            <PartnerDisclosures disclosureParagraphs={disclosure_paragraphs} />
          )}
          <MesaFooter style={{ width: '100%' }} />
        </>
      }
      insetTopProps={{ style: { backgroundColor: cardApplicationEntryBackgroundColor } }}
      testID={CardApplication.E2E.Config.Ids.View.CardBenefits}
    >
      <EnablePermissionsBanner text="Never miss any of Mesa's great offers and programs!" />

      <View style={{ flex: 1, paddingHorizontal: 0, paddingVertical: 0 }}>
        <DesignSystem.Card
          style={{
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: 16,
            gap: 16,
            flex: 1,
            width: '100%',
          }}
        >
          <MesaHeader>
            <MesaMonogram />
            <MesaCardImage style={{ width: 260, position: 'absolute' }} />
          </MesaHeader>
          <Text style={[{ marginHorizontal: 16 }, typography['heading/heading3']]}>
            {headerText}
          </Text>
          <View style={{ marginHorizontal: 16, gap: 8 }}>
            {benefits
              .filter(({ is_enabled }) => is_enabled)
              .map(({ markdown_text }) => (
                <CardFeature key={markdown_text} text={markdown_text} />
              ))}
          </View>
          <Text style={[{ marginHorizontal: 16 }, typography['heading/heading5']]}>
            Enjoy no Annual Fee² - More Savings, More Rewards!
          </Text>
          {!isShowingCardInviteCodeSubmissions || showCardApplyButton ? (
            cardApplicationRoutesEnabled ? (
              <Button
                containerStyle={styles.button}
                disabled={!isButtonEnabled}
                {...(isButtonEnabled ? undefined : { variant: 'outlined' })}
                testID={showCardApplyButton ? 'CardApplication.Press.Apply' : ''}
                onPress={() => {
                  if (showCardApplyButton) {
                    void triggerSegmentAction({
                      component: 'Button',
                      action: 'MesaCard Button Apply',
                    })
                    navigateToApplicationFlow({
                      cardApplicationStatus: cardApplicationStatus?.getCardProductApplicationStatus
                        .status
                        ? {
                            legacy: cardApplicationStatus.getCardProductApplicationStatus.status,
                          }
                        : newestApplicationQuery.data?.newestApplication?.status
                          ? {
                              forNewestApplication:
                                newestApplicationQuery.data.newestApplication.status,
                            }
                          : undefined,
                      navigateTo: (route) => router.push(route),
                    })
                  } else {
                    void triggerSegmentAction({
                      component: 'Button',
                      action: 'User Pressed the Join Waitlist Button for Mesa Card',
                    })
                    void joinWaitlist()
                  }
                }}
              >
                {buttonContent}
              </Button>
            ) : (
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  paddingHorizontal: 16,
                  justifyContent: 'center',
                }}
              >
                <Tooltip
                  triggerProps={{
                    style: {
                      alignSelf: 'center',
                      width: '100%',
                      maxWidth: buttonMaxWidth,
                      margin: 'auto',
                    },
                  }}
                  delay={88}
                  arrowProps={{
                    style: { backgroundColor: mesaColorNames['Acre Turquoise/500 #00DFC9'] },
                  }}
                  content={
                    <Text
                      style={[
                        typography['label/sm-regular'],
                        {
                          backgroundColor: mesaColorNames['Acre Turquoise/500 #00DFC9'],
                          padding: 16,
                          borderRadius: 16,
                          color: mesaColorNames['Greyscale/900 #152438'],
                        },
                      ]}
                      children="Card applications on web are coming soon!"
                    />
                  }
                >
                  <MobileAppInstallCta children="Install the Mesa App to Apply" />
                </Tooltip>
              </View>
            )
          ) : null}
          {isMesaCardInviteEnabled &&
            !isShowingCardInviteCodeSubmissions &&
            !showCardApplyButton &&
            !isMesaCardEnabled && (
              <Button
                onPress={() => setIsShowingCardInviteCodeSubmissions(true)}
                variant="text"
                title="Enter Invite Code"
              />
            )}
          {isShowingCardInviteCodeSubmissions && !showCardApplyButton && (
            <Input
              placeholder="Invite Code"
              onChangeText={setCardInviteCodeInput}
              info={{ error: cardInviteCodeError }}
              maxLength={6}
              containerStyle={{ marginLeft: 16, marginRight: 16 }}
            />
          )}
          {isShowingCardInviteCodeSubmissions && !showCardApplyButton && (
            <Button containerStyle={styles.button} onPress={() => void verifyInviteCode()}>
              <Text style={styles.footerButtonText}>Submit Invite Code</Text>
            </Button>
          )}
        </DesignSystem.Card>

        <View style={{ marginTop: 16, gap: 8 }}>
          {disclosures
            .filter(({ is_enabled }) => is_enabled)
            .map(({ markdown_text }) => (
              <DesignSystem.Markdown
                key={markdown_text}
                style={{
                  body: { color: mesaColorNames['Greyscale/400 #A4AAAC'] },
                  paragraph: { marginTop: 0, marginBottom: 0 },
                }}
                children={markdown_text}
              />
            ))}
        </View>
      </View>
    </MainWrapper>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    gap: 4,
  },
  squareCardImageContainer: {
    flex: 1,
    alignItems: 'center',
    paddingTop: 60,
  },
  squareCardImage: {
    height: 210,
    width: 370,
    marginVertical: 10,
  },
  titleLabel: {
    color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
    paddingTop: 20,
    paddingBottom: 5,
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
  },
  membershipDetailsTitle: {
    fontSize: 16,
    marginBottom: 5,
  },
  // card feature
  cardFeatureContainer: {
    gap: 10,
  },
  cardFeature: {
    flexShrink: 1,
    flexDirection: 'row',
    paddingRight: 20,
  },
  cardFeatureIcon: {
    marginRight: 10,
  },
  button: {
    paddingHorizontal: 16,
  },
  footerButtonProcessingWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  footerButtonText: {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 16,
  },
  footerButtonTextProcessing: {
    color: mesaColorNames['Acre Turquoise/800 #086762'],
  },
  termsText: {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 14,
    color: mesaColorNames['Greyscale/400 #A4AAAC'],
    padding: 6,
  },
  termsTextLink: {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 14,
    color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
    padding: 6,
    textDecorationLine: 'underline',
  },
})
